export const ACQUISITION_ATTRIBUTION_CHANGED =
	"verifieddr:acquisition-attribution-changed";

export const SIGNUP_ATTRIBUTION_COOKIE = "verifieddr_signup_attribution";

const STORAGE_KEY = "verifieddr:acquisition-attribution:v1";
const MAX_VALUE_LENGTH = 512;
const SIGNUP_ATTRIBUTION_WINDOW_MS = 24 * 60 * 60 * 1000;
const SIGNUP_ATTRIBUTION_COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;

const paidClickParameters = [
	["fbclid", "meta"],
	["twclid", "x"],
	["gclid", "google"],
	["msclkid", "microsoft"],
] as const;

const paidMediums = new Set([
	"cpc",
	"paid",
	"paid-social",
	"paid_social",
	"ppc",
]);

export type AcquisitionTouch = {
	source: string;
	medium: string;
	campaign?: string;
	campaignId?: string;
	content?: string;
	term?: string;
	adSetId?: string;
	adId?: string;
	clickId?: string;
	clickIdType?: string;
	referringDomain?: string;
	landingPath: string;
	capturedAt: string;
};

export type StoredAcquisitionAttribution = {
	version: 1;
	firstTouch: AcquisitionTouch;
	lastTouch: AcquisitionTouch;
	lastPaidTouch?: AcquisitionTouch;
};

export type SignupAcquisitionChannel = Pick<
	AcquisitionTouch,
	"source" | "medium"
> &
	Partial<Pick<AcquisitionTouch, "campaign">>;

function cleanString(value: unknown, maxLength = MAX_VALUE_LENGTH) {
	if (typeof value !== "string") return undefined;
	const clean = value.trim().slice(0, maxLength);
	return clean || undefined;
}

function queryValue(params: URLSearchParams, name: string) {
	return cleanString(params.get(name), 256);
}

function referringDomain(referrer: string | undefined, landing: URL) {
	if (!referrer) return undefined;
	try {
		const hostname = new URL(referrer).hostname.toLowerCase();
		if (!hostname || hostname === landing.hostname.toLowerCase())
			return undefined;
		return hostname.replace(/^www\./, "").slice(0, 253);
	} catch {
		return undefined;
	}
}

function clickAttribution(params: URLSearchParams) {
	for (const [parameter, source] of paidClickParameters) {
		const clickId = queryValue(params, parameter);
		if (clickId) return { clickId, clickIdType: parameter, source };
	}
	return undefined;
}

export function buildAcquisitionTouch(input: {
	url: string;
	referrer?: string;
	capturedAt?: string;
}): AcquisitionTouch | null {
	let landing: URL;
	try {
		landing = new URL(input.url);
	} catch {
		return null;
	}

	const params = landing.searchParams;
	const click = clickAttribution(params);
	const utmSource = queryValue(params, "utm_source");
	const utmMedium = queryValue(params, "utm_medium");
	const domain = referringDomain(input.referrer, landing);
	const source = utmSource ?? click?.source ?? domain ?? "direct";
	const medium = utmMedium ?? (click ? "cpc" : domain ? "referral" : "none");

	return {
		source,
		medium,
		...(queryValue(params, "utm_campaign")
			? { campaign: queryValue(params, "utm_campaign") }
			: {}),
		...((queryValue(params, "utm_id") ?? queryValue(params, "campaign_id"))
			? {
					campaignId:
						queryValue(params, "utm_id") ?? queryValue(params, "campaign_id"),
				}
			: {}),
		...(queryValue(params, "utm_content")
			? { content: queryValue(params, "utm_content") }
			: {}),
		...(queryValue(params, "utm_term")
			? { term: queryValue(params, "utm_term") }
			: {}),
		...((queryValue(params, "adset_id") ?? queryValue(params, "adgroup_id"))
			? {
					adSetId:
						queryValue(params, "adset_id") ?? queryValue(params, "adgroup_id"),
				}
			: {}),
		...((queryValue(params, "ad_id") ?? queryValue(params, "creative_id"))
			? {
					adId:
						queryValue(params, "ad_id") ?? queryValue(params, "creative_id"),
				}
			: {}),
		...(click
			? { clickId: click.clickId, clickIdType: click.clickIdType }
			: {}),
		...(domain ? { referringDomain: domain } : {}),
		landingPath: landing.pathname.slice(0, MAX_VALUE_LENGTH) || "/",
		capturedAt: input.capturedAt ?? new Date().toISOString(),
	};
}

function isPaidAcquisitionTouch(touch: AcquisitionTouch) {
	return Boolean(touch.clickId) || paidMediums.has(touch.medium.toLowerCase());
}

export function isPlausibleSignupFirstTouch(
	touch: AcquisitionTouch,
	userCreatedAt: string | number | Date,
) {
	const touchTime = new Date(touch.capturedAt).getTime();
	const createdTime = new Date(userCreatedAt).getTime();
	return (
		Number.isFinite(touchTime) &&
		Number.isFinite(createdTime) &&
		touchTime <= createdTime + SIGNUP_ATTRIBUTION_WINDOW_MS
	);
}

export function mergeAcquisitionTouch(
	current: StoredAcquisitionAttribution | null,
	touch: AcquisitionTouch,
): StoredAcquisitionAttribution {
	const firstTouch = current?.firstTouch ?? touch;
	const lastTouch =
		touch.source === "direct" && current?.lastTouch ? current.lastTouch : touch;
	const lastPaidTouch = isPaidAcquisitionTouch(touch)
		? touch
		: current?.lastPaidTouch;

	return {
		version: 1,
		firstTouch,
		lastTouch,
		...(lastPaidTouch ? { lastPaidTouch } : {}),
	};
}

function normalizeAcquisitionTouch(value: unknown): AcquisitionTouch | null {
	if (!value || typeof value !== "object") return null;
	const row = value as Record<string, unknown>;
	const source = cleanString(row.source, 256);
	const medium = cleanString(row.medium, 256);
	const landingPath = cleanString(row.landingPath, MAX_VALUE_LENGTH);
	const capturedAt = cleanString(row.capturedAt, 64);
	if (!source || !medium || !landingPath || !capturedAt) return null;
	if (!Number.isFinite(new Date(capturedAt).getTime())) return null;

	const optional = (name: keyof AcquisitionTouch, max = 256) => {
		const value = cleanString(row[name], max);
		return value ? { [name]: value } : {};
	};

	return {
		source,
		medium,
		...optional("campaign"),
		...optional("campaignId"),
		...optional("content"),
		...optional("term"),
		...optional("adSetId"),
		...optional("adId"),
		...optional("clickId", MAX_VALUE_LENGTH),
		...optional("clickIdType", 32),
		...optional("referringDomain", 253),
		landingPath,
		capturedAt: new Date(capturedAt).toISOString(),
	};
}

export function normalizeStoredAttribution(
	value: unknown,
): StoredAcquisitionAttribution | null {
	if (!value || typeof value !== "object") return null;
	const row = value as Record<string, unknown>;
	const firstTouch = normalizeAcquisitionTouch(row.firstTouch);
	const lastTouch = normalizeAcquisitionTouch(row.lastTouch);
	const lastPaidTouch = normalizeAcquisitionTouch(row.lastPaidTouch);
	if (!firstTouch || !lastTouch) return null;
	return {
		version: 1,
		firstTouch,
		lastTouch,
		...(lastPaidTouch && isPaidAcquisitionTouch(lastPaidTouch)
			? { lastPaidTouch }
			: {}),
	};
}

export function parseSignupAttributionCookie(
	value: string | null | undefined,
): SignupAcquisitionChannel | null {
	if (!value) return null;
	let parsed: unknown;
	try {
		parsed = JSON.parse(value);
	} catch {
		try {
			parsed = JSON.parse(decodeURIComponent(value));
		} catch {
			return null;
		}
	}
	if (!parsed || typeof parsed !== "object") return null;
	const row = parsed as Record<string, unknown>;
	const source = cleanString(row.source, 256);
	const medium = cleanString(row.medium, 256);
	const campaign = cleanString(row.campaign, 256);
	if (!source || !medium) return null;
	return { source, medium, ...(campaign ? { campaign } : {}) };
}

function writeSignupAttributionCookie(
	attribution: StoredAcquisitionAttribution,
) {
	if (typeof document === "undefined" || typeof window === "undefined") return;
	const touch = attribution.lastTouch;
	const value = encodeURIComponent(
		JSON.stringify({
			source: touch.source,
			medium: touch.medium,
			...(touch.campaign ? { campaign: touch.campaign } : {}),
		}),
	);
	const secure = window.location.protocol === "https:" ? "; Secure" : "";
	// biome-ignore lint/suspicious/noDocumentCookie: the next auth request and OAuth callback need this first-party value
	document.cookie = `${SIGNUP_ATTRIBUTION_COOKIE}=${value}; Path=/; Max-Age=${SIGNUP_ATTRIBUTION_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
}

export function readStoredAttribution(): StoredAcquisitionAttribution | null {
	if (typeof window === "undefined") return null;
	try {
		return normalizeStoredAttribution(
			JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? "null"),
		);
	} catch {
		return null;
	}
}

export function clearStoredAttribution() {
	if (typeof window === "undefined") return;
	try {
		window.localStorage.removeItem(STORAGE_KEY);
	} catch {
		// Storage can be unavailable in privacy-restricted browsers.
	}
}

export function captureCurrentAttribution() {
	if (typeof window === "undefined") return null;
	const touch = buildAcquisitionTouch({
		url: window.location.href,
		referrer: document.referrer,
	});
	if (!touch) return null;
	const previous = readStoredAttribution();
	const next = mergeAcquisitionTouch(previous, touch);
	try {
		// The auth hook runs before the signed-in client can sync attribution to
		// Convex. A small first-party cookie carries only the channel through
		// email signup and OAuth callbacks so the signup alert can include it.
		writeSignupAttributionCookie(next);
	} catch {
		// Attribution must never block the page in cookie-restricted browsers.
	}
	try {
		const previousJson = previous ? JSON.stringify(previous) : null;
		const nextJson = JSON.stringify(next);
		window.localStorage.setItem(STORAGE_KEY, nextJson);
		if (previousJson !== nextJson) {
			window.dispatchEvent(new Event(ACQUISITION_ATTRIBUTION_CHANGED));
		}
	} catch {
		// Attribution must never block the page in storage-restricted browsers.
	}
	return next;
}

function touchProperties(prefix: string, touch: AcquisitionTouch | undefined) {
	if (!touch) return {};
	return {
		[`${prefix}_source`]: touch.source,
		[`${prefix}_medium`]: touch.medium,
		...(touch.campaign ? { [`${prefix}_campaign`]: touch.campaign } : {}),
		...(touch.campaignId
			? { [`${prefix}_campaign_id`]: touch.campaignId }
			: {}),
		...(touch.content ? { [`${prefix}_content`]: touch.content } : {}),
		...(touch.term ? { [`${prefix}_term`]: touch.term } : {}),
		...(touch.adSetId ? { [`${prefix}_ad_set_id`]: touch.adSetId } : {}),
		...(touch.adId ? { [`${prefix}_ad_id`]: touch.adId } : {}),
		...(touch.clickId ? { [`${prefix}_click_id`]: touch.clickId } : {}),
		...(touch.clickIdType
			? { [`${prefix}_click_id_type`]: touch.clickIdType }
			: {}),
		...(touch.referringDomain
			? { [`${prefix}_referring_domain`]: touch.referringDomain }
			: {}),
		[`${prefix}_landing_path`]: touch.landingPath,
		[`${prefix}_captured_at`]: touch.capturedAt,
	};
}

export function postHogAcquisitionProperties(
	attribution: StoredAcquisitionAttribution,
	options?: { includeFirstTouch?: boolean },
) {
	return {
		set: {
			...touchProperties("acquisition_last", attribution.lastTouch),
			...touchProperties("acquisition_paid", attribution.lastPaidTouch),
		},
		setOnce:
			options?.includeFirstTouch === false
				? {}
				: touchProperties("acquisition_first", attribution.firstTouch),
	};
}
